import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useSubscription, PLAN_CONFIG } from '../../context/SubscriptionContext';
import RemindersSettings from './RemindersSettings';
import { apiExportData, apiDeleteAccount, apiCreatePortalSession, clearToken } from '../../services/api';

const { FiChevronLeft, FiDownload, FiTrash2, FiShield, FiBell, FiClock, FiCheck, FiCreditCard, FiZap, FiAlertCircle } = FiIcons;

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`w-12 h-7 rounded-full p-1 transition-colors duration-200 ease-in-out flex items-center ${
        checked ? 'bg-stone-900' : 'bg-stone-200'
      }`}
    >
      <motion.div
        layout
        className="w-5 h-5 rounded-full bg-white shadow-sm"
        animate={{ x: checked ? 20 : 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      />
    </button>
  );
}

function NotificationsSettings() {
  const [settings, setSettings] = useState({
    daily: true,
    weekly: true,
    streaks: true,
    marketing: false,
  });

  const toggle = (key) => setSettings((s) => ({ ...s, [key]: !s[key] }));

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm overflow-hidden p-1">
        <div className="flex items-center justify-between p-4 border-b border-stone-50">
          <div>
            <p className="font-sans font-medium text-stone-800">Daily Reminders</p>
            <p className="font-sans text-xs text-stone-400 mt-0.5">Don't forget to log your skin</p>
          </div>
          <Toggle checked={settings.daily} onChange={() => toggle('daily')} />
        </div>
        <div className="flex items-center justify-between p-4 border-b border-stone-50">
          <div>
            <p className="font-sans font-medium text-stone-800">Streak Alerts</p>
            <p className="font-sans text-xs text-stone-400 mt-0.5">Get notified when you hit milestones</p>
          </div>
          <Toggle checked={settings.streaks} onChange={() => toggle('streaks')} />
        </div>
        <div className="flex items-center justify-between p-4">
          <div>
            <p className="font-sans font-medium text-stone-800">Weekly Insights</p>
            <p className="font-sans text-xs text-stone-400 mt-0.5">Summary of your glow trends</p>
          </div>
          <Toggle checked={settings.weekly} onChange={() => toggle('weekly')} />
        </div>
      </div>
    </div>
  );
}

function ReminderTimeSettings() {
  const [time, setTime] = useState('20:00');
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-6 text-center">
        <SafeIcon icon={FiClock} className="text-3xl text-stone-300 mx-auto mb-4" />
        <p className="font-sans text-sm text-stone-500 mb-6">
          When would you like us to remind you to log your daily skin entry?
        </p>
        
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="text-4xl font-serif text-stone-900 bg-stone-50 rounded-2xl px-6 py-4 border border-stone-100 focus:outline-none focus:border-stone-300 w-full text-center appearance-none"
        />

        <button
          onClick={handleSave}
          className="w-full mt-8 py-3.5 rounded-full font-sans font-medium text-white shadow-sm transition-all"
          style={{ background: saved ? '#5C8A78' : 'linear-gradient(135deg, #C07A52, #E8C97A)' }}
        >
          {saved ? 'Saved!' : 'Update Reminder Time'}
        </button>
      </div>
    </div>
  );
}

function ExportDataSettings() {
  const [exporting, setExporting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const blob = await apiExportData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `glowlytix-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setDone(true);
      setTimeout(() => setDone(false), 3000);
    } catch {
      setError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-6">
        <SafeIcon icon={FiDownload} className="text-3xl text-stone-300 mx-auto mb-4 block" />
        <p className="font-sans text-sm text-stone-500 text-center mb-6">
          Download a full copy of your entries, photos, insights, and routines as JSON. Your data is yours.
        </p>
        {error && <p className="font-sans text-xs text-red-500 text-center mb-4">{error}</p>}
        <button
          onClick={handleExport}
          disabled={exporting}
          className="w-full py-3.5 rounded-2xl font-sans font-medium text-stone-700 bg-stone-50 border border-stone-200 flex items-center justify-center gap-2 active:bg-stone-100 transition-colors disabled:opacity-60"
        >
          {done ? <SafeIcon icon={FiCheck} className="text-emerald-500" /> : <SafeIcon icon={FiDownload} />}
          {exporting ? 'Exporting…' : done ? 'Downloaded!' : 'Export My Data (JSON)'}
        </button>
      </div>
    </div>
  );
}

function BillingSettings({ onOpenPricing }) {
  const { planId, plan, isOnTrial, trialDaysLeft } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);

  const openPortal = async () => {
    setPortalLoading(true);
    setPortalError(null);
    try {
      const { url } = await apiCreatePortalSession();
      window.location.href = url;
    } catch (err) {
      setPortalError(err.message || 'Could not open billing portal. Please try again.');
      setPortalLoading(false);
    }
  };

  const allPlans = Object.values(PLAN_CONFIG);

  return (
    <div className="space-y-5">
      {/* Current Plan */}
      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-stone-50">
          <p className="font-sans text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3">Current Plan</p>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-serif text-xl text-stone-900">{plan.name}</span>
                <span
                  className="font-sans text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{ background: `${plan.accent}20`, color: plan.accent }}
                >
                  {planId === 'free' ? 'Free' : 'Active'}
                </span>
              </div>
              <p className="font-sans text-sm text-stone-500">
                {plan.price === 0 ? 'Free forever' : `$${plan.price}/year`}
              </p>
              {isOnTrial && (
                <p className="font-sans text-xs text-amber-600 mt-0.5 font-medium">
                  Free trial · {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining
                </p>
              )}
            </div>
            <button
              onClick={onOpenPricing}
              className="px-4 py-2 rounded-full font-sans text-sm font-semibold text-white shadow-sm"
              style={{ background: `linear-gradient(135deg, ${plan.accent}, ${planId === 'radiant' ? '#a855f7' : '#7ecdc4'})` }}
            >
              {planId === 'free' ? 'Upgrade' : 'Change'}
            </button>
          </div>
        </div>

        {/* Plan comparison stacked list */}
        <div className="px-4 pb-4 space-y-2">
          {allPlans.map((p) => (
            <button
              key={p.id}
              onClick={() => p.id !== planId && onOpenPricing()}
              className="w-full flex items-center justify-between px-4 py-3 rounded-2xl border font-sans text-sm font-medium transition-all active:scale-[0.98]"
              style={
                planId === p.id
                  ? { borderColor: p.accent, color: p.accent, background: `${p.accent}15` }
                  : { borderColor: '#e7e5e4', color: '#a8a29e', background: 'white' }
              }
            >
              <span>{p.name}</span>
              <span className="font-sans text-xs">{p.price > 0 ? `$${p.price}/yr` : 'Free'}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Manage Subscription via Stripe Portal */}
      {planId !== 'free' && (
        <div className="space-y-3">
          {portalError && (
            <p className="font-sans text-xs text-red-500 text-center bg-red-50 rounded-xl py-2 px-3">{portalError}</p>
          )}
          <button
            onClick={openPortal}
            disabled={portalLoading}
            className="w-full py-4 rounded-2xl font-sans font-medium text-stone-700 bg-white border border-stone-200 flex items-center justify-center gap-2 active:bg-stone-50 disabled:opacity-60 shadow-sm"
          >
            <SafeIcon icon={FiCreditCard} />
            {portalLoading ? 'Opening portal…' : 'Manage Billing & Payment'}
          </button>
          <p className="font-sans text-xs text-stone-400 text-center">
            Update card, view invoices, or cancel — via Stripe's secure portal
          </p>
        </div>
      )}
    </div>
  );
}

function PrivacySettings() {
  const [analytics, setAnalytics] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiDeleteAccount();
      clearToken();
      window.location.href = '/';
    } catch {
      setDeleteError('Deletion failed. Please try again or contact support.');
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Medical Disclaimer — prominent, top of privacy */}
      <div className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-5">
        <div className="flex items-start gap-3">
          <span className="text-2xl flex-shrink-0">⚕️</span>
          <div>
            <p className="font-sans text-sm font-bold text-amber-900 mb-2">Not Medical Advice</p>
            <p className="font-sans text-xs text-amber-800 leading-relaxed">
              Glowlytix is a personal skin tracking tool for informational and self-monitoring purposes only. Under no circumstances does this service provide medical advice, diagnosis, or treatment recommendations.
            </p>
            <p className="font-sans text-xs text-amber-800 leading-relaxed mt-2">
              AI-generated skin insights are based solely on your logged data and are not a substitute for professional dermatological evaluation. Always consult a qualified healthcare provider or licensed dermatologist for any skin condition, concern, or treatment decision.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-6 text-center">
        <SafeIcon icon={FiShield} className="text-4xl text-emerald-400 mx-auto mb-4 block opacity-80" />
        <h3 className="font-serif text-xl text-stone-900 mb-2">Your Data</h3>
        <p className="font-sans text-sm text-stone-500">
          Your entries and photos are stored securely on our servers and transmitted over HTTPS. We do not sell your data or share it with advertisers.
        </p>
      </div>

      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-4 flex items-center justify-between">
        <div>
          <p className="font-sans font-medium text-stone-800">Share Analytics</p>
          <p className="font-sans text-xs text-stone-400 mt-0.5 max-w-[200px]">Help improve the app by sharing anonymous usage data</p>
        </div>
        <Toggle checked={analytics} onChange={setAnalytics} />
      </div>

      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-4">
        <p className="font-sans text-xs text-stone-400 mb-2">Legal</p>
        <div className="flex gap-4">
          <Link to="/privacy" className="font-sans text-sm text-stone-600 underline underline-offset-2">Privacy Policy</Link>
          <Link to="/terms" className="font-sans text-sm text-stone-600 underline underline-offset-2">Terms of Service</Link>
        </div>
      </div>

      <div className="pt-2">
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full py-4 rounded-2xl font-sans font-medium text-red-500 bg-red-50 border border-red-100 flex items-center justify-center gap-2 active:bg-red-100 transition-colors"
        >
          <SafeIcon icon={FiTrash2} /> Delete Account &amp; All Data
        </button>
        <p className="font-sans text-xs text-stone-400 text-center mt-3 uppercase tracking-wider">This action cannot be undone</p>
      </div>

      <AnimatePresence>
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-end bg-black/30 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)}>
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full bg-white rounded-t-3xl px-6 pb-10 pt-6 shadow-2xl"
            >
              <div className="w-10 h-1 bg-stone-200 rounded-full mx-auto mb-6" />
              <h3 className="font-serif text-2xl text-stone-900 text-center mb-2">Delete your account?</h3>
              <p className="font-sans text-sm text-stone-500 text-center mb-2 leading-relaxed">
                This will permanently delete your account, all skin entries, photos, and AI insights. This cannot be undone.
              </p>
              {deleteError && <p className="font-sans text-xs text-red-500 text-center mb-4">{deleteError}</p>}
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="w-full py-4 rounded-full font-sans font-semibold text-white bg-red-500 mb-3 disabled:opacity-60"
              >
                {deleting ? 'Deleting…' : 'Yes, Delete Everything'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full py-4 rounded-full font-sans font-medium text-stone-500 bg-stone-50 border border-stone-200"
              >
                Cancel
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function SettingsOverlay({ activeSetting, onClose, onOpenPricing, entries }) {
  if (!activeSetting) return null;

  const renderContent = () => {
    switch (activeSetting) {
      case 'Reminders': return <RemindersSettings entries={entries} />;
      case 'Export Data': return <ExportDataSettings />;
      case 'Privacy': return <PrivacySettings />;
      case 'Subscription & Billing': return <BillingSettings onOpenPricing={() => { onClose(); onOpenPricing(); }} />;
      default: return <div className="p-4 text-stone-500 font-sans">Coming soon...</div>;
    }
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      className="absolute inset-0 z-50 bg-stone-50 flex flex-col"
    >
      <div className="pt-8 pb-4 px-4 flex items-center gap-3 bg-white border-b border-stone-100 flex-shrink-0 z-10 shadow-sm">
        <button 
          onClick={onClose}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-stone-50 text-stone-600 active:bg-stone-100 transition-colors"
        >
          <SafeIcon icon={FiChevronLeft} className="text-xl" />
        </button>
        <h2 className="font-serif text-2xl text-stone-900">{activeSetting}</h2>
      </div>
      
      <div className="flex-1 overflow-y-auto p-5">
        {renderContent()}
      </div>
    </motion.div>
  );
}